const moment = require('moment');

const TIMESTAMP_FORMAT = 'YYYYMMDDHHmmssSSS';
const DATE_FORMAT = 'YYYY-MM-DD';
const TIME_FORMAT1 = 'HH:mm:ss';
const TIME_FORMAT2 = 'HHmm';
const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
const DATEHOUR_FORMAT = 'YYYYMMDDHH';
const DATE_FORMAT_VARIANT1 = 'MMM Do dddd';
const DATE_FORMAT_VARIANT2 = 'YY/MM/DD(ddd)';
const DATE_FORMAT_VARIANT3 = 'YYYY년 M월 D일 HH:mm';
const DATE_FORMAT_SHORTCUT = 'M/D(ddd)';

const timestamp = (d) => moment(d).format(TIMESTAMP_FORMAT);
const date = (d) => moment(d).format(DATE_FORMAT);
const time = (d) => moment(d).format(TIME_FORMAT1);
const datetime = (d) => moment(d).format(DATETIME_FORMAT);
const utc = (d) => moment(d).toISOString();

//---------------------------------------------------------

const now = moment();

//---------------------------------------------------------
// parse

/**
 *
 * @param [s] {String} 날짜/시간 문자열. 타임존 정보가 없으면 로컬 타임존.
 * @param [fmt] {String} 날짜/시각 형식
 * @param [fallback] {moment.Moment} 변환이 불가할 때 반환할 값
 * @returns {moment.Moment}
 */
const parse = (s, fmt, fallback) => {
  try {
    const m = moment(s, fmt);
    // 형식을 지정하면 moment 휴리스틱 사용 안함
    // const m = moment(s, fmt, !!fmt);
    return m.isValid() ? m : fallback;
  } catch (e) {
    console.assert(e, '**ignore** parse error:', e, s, fmt);
    return fallback;
  }
};

/**
 * @param [s] {String} YYYYMMDDHHmmssSSS 형식 + moment 휴리스틱
 */
const parseTimestamp = (s, fallback) => parse(s, TIMESTAMP_FORMAT, fallback);
/**
 * @param [s] {String} YYYY-MM-DD HH:mm:ss 형식(mysql datetime 컬럼) + moment 휴리스틱
 */
const parseDatetime = (s, fallback) => parse(s, DATETIME_FORMAT, fallback);
/**
 *  @param [s] {String} YYYY-MM-DD 형식 + moment 휴리스틱
 */
const parseDate = (s, fallback) => parse(s, DATE_FORMAT, fallback);
/**
 *  @param [s] {String} HH:mm:ss 형식 + moment 휴리스틱
 */
const parseTime = (s, fallback) => parse(s, TIME_FORMAT1, fallback);

//---------------------------------------------------------
// format

/**
 *
 * @param [d] {Date|moment.Moment|String|Number} 날짜/시각 문자열, epoch, 또는 객체
 * @param [fmt] {String} 날짜/시각 형식
 * @param [fallback] {String} 변환이 불가할 때 반환할 값
 * @return {String}
 */
const format = (d, fmt, fallback) => {
  try {
    const m = moment.isMoment(d) ? d : moment(d);
    return m.isValid() ? m.format(fmt) : fallback;
  } catch (e) {
    console.assert(e, '**ignore** format error:', e, d, fmt);
    return fallback;
  }
};

/**
 * @return {String} YYYYMMDDHHmmssSSS 형식
 */
const formatTimestamp = (d, fallback) => format(d, TIMESTAMP_FORMAT, fallback);
/**
 * @return {String} YYYY-MM-DD HH:mm:ss 형식(mysql datetime 컬럼)
 */
const formatDatetime = (d, fallback) => format(d, DATETIME_FORMAT, fallback);
/**
 * @return {String} YYYYMMDDHH 형식
 */
const formatDatehour = (d, fallback) => format(d, DATEHOUR_FORMAT, fallback);
/**
 * @return {String} YYYY-MM-DD 형식
 */
const formatDate = (d, fallback) => format(d, DATE_FORMAT, fallback);
/**
 * @return {String} HH:mm:ss 형식
 */
const formatTime = (d, fallback) => format(d, TIME_FORMAT1, fallback);
/**
 * @return {String} MMM월 D일 dddd요일 {정오|자정|H} 형식
 * 1월 31일 24시(2월 1일 00시) -> 1월 31일 자정으로 반환해야 합니다
 */
const formatLocalDate = (d, fallback) => {
  const now = utcOffset(d, 9, fallback);
  const hours = now.format('HH');
  now.locale('ko');

  const str = format(now, DATE_FORMAT_VARIANT2, fallback);

  if (hours === '00') {
    return `${str} 자정`;
  } else if (hours === '12') {
    return `${str} 정오`;
  } else {
    return `${str} ${hours}시`;
  }
};
/**
 * @return {String} YY/MM/DD(ddd) {정오|자정|H} 형식
 */
const formatLocalDatehourVariant1 = (d, fallback) => {
  const now = utcOffset(d, 9, fallback);
  const hourMin = now.format(TIME_FORMAT2);

  now.locale('ko');

  if (hourMin === '0000') {
    const str = format(now.subtract(1, 'days'), DATE_FORMAT_VARIANT1, fallback);
    return `${str} 자정`;
  } else {
    const str = format(now, DATE_FORMAT_VARIANT1, fallback);
    if (hourMin === '2359') {
      return `${str} 자정`;
    }
    if (hourMin === '1200') {
      return `${str} 정오`;
    }
    const hour = now.hour();
    return `${str} ${hour}시`;
  }
};

/**
 * @return {String} YYYY년MM월DD일 HH:mm 형식
 */
const formatLocalDatetimeVariant1 = (d, timezone = 9, fallback) => {
  const now = utcOffset(d, timezone, fallback);

  return format(now, DATE_FORMAT_VARIANT3, fallback);
};

/**
 * @return {String} M/D(ddd) {정오|자정|H} 형식
 * 1월 31일 24시(2월 1일 00시) -> 1/31(요일) 자정으로 반환해야 합니다
 */
const formatLocalDateShortcut = (d, fallback) => {
  const now = utcOffset(d, 9, fallback);
  const hourMin = now.format('HHmm');

  now.locale('ko');

  if (hourMin === '0000') {
    const str = format(now.subtract(1, 'days'), DATE_FORMAT_SHORTCUT, fallback);
    return `${str} 자정`;
  } else {
    const str = format(now, DATE_FORMAT_SHORTCUT, fallback);
    if (hourMin === '2359') {
      return `${str} 자정`;
    }
    if (hourMin === '1200') {
      return `${str} 정오`;
    }
    const hour = now.hour();
    return `${str} ${hour}시`;
  }
};

const utcOffset = (d, utcOffset = 0, fallback) => {
  try {
    const m = moment.isMoment(d) ? d : moment(d);
    return m.isValid() ? m.utc().utcOffset(utcOffset) : d;
  } catch (e) {
    console.assert(e, '**ignore** utcOffset error:', e, d, utcOffset);
    return fallback;
  }
};

const momentRelativeLocales = {
  relativeTime: {
    future: '%s 후',
    past: '%s 전',
    s: '금방',
    ss: '%d초',
    m: '1분',
    mm: '%d분',
    h: '1시간',
    hh: '%d시간',
    d: '1일',
    dd: '%d일',
    M: '1달',
    MM: '%d달',
    y: '1년',
    yy: '%d년',
  },
};

const updateMomentLocaleToKorean = (moment) => {
  moment.updateLocale('ko', momentRelativeLocales);
};

const durationAsSecond = (str) => moment.duration(str).asSeconds();
const parseByUnix = (str) => moment.unix(str);
const parseByUtc = (str) => moment.utc(str);
const supressDeprecationWarnings = (booleanValue) => {
  moment.supressDeprecationWarnings = !!booleanValue;
};
const isMoment = (suspect) => moment.isMoment(suspect);

const diff = (since, until, type = 'd') => {
  return moment(until).diff(moment(since), type);
};

/**
 * @param [s] {number} 초
 * @returns {string} ex) 00:00:00
 */
const secondsToTimeFormat = (seconds, fallback = '') => {
  if (!Number.isInteger(seconds) || seconds < 0) {
    return typeof fallback === 'function' ? fallback(seconds) : fallback;
  }

  const hh = Math.floor(seconds / 3600)
    .toString()
    .padStart(2, '0');
  const mm = Math.floor((seconds % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const ss = (seconds % 60).toString().padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
};

/**
 * @param [minutes] {number} 분
 * @returns {string} ex) 10시간 30분
 */
const minutesToHourFormat = (minutes) => {
  const hh = Math.floor(minutes / 60);
  const quotient = minutes % 60;

  if (hh === 0) {
    return `${minutes}분`;
  } else {
    return `${hh}시간 ${quotient}분`;
  }
};

/**
 * @param [second] {number} 초
 * @returns {string} ex) 10시간 30분
 */
const secondToHourFormat = (second) => {
  const minutes = Math.floor(second / 60);
  return minutesToHourFormat(minutes);
};

/**
 * @param [second] {number} 초
 * @returns {string} ex) 90분 20초
 */
const secondsToMinuteFormat = (second) => {
  const minutes = Math.floor(second / 60);
  const quotient = second % 60;

  if (minutes === 0) {
    return `${minutes}분`;
  } else {
    return `${minutes}분 ${quotient}초`;
  }
};

module.exports = {
  timestamp, // deprecated
  date, // deprecated
  time, // deprecated
  datetime, // deprecated
  utc, // deprecated
  now,
  parse,
  parseTimestamp,
  parseDatetime,
  parseDate,
  parseTime,
  format,
  formatTimestamp,
  formatDatetime,
  formatDatehour,
  formatDate,
  formatTime,
  utcOffset,
  formatLocalDatehourVariant1,
  formatLocalDatetimeVariant1,
  formatLocalDateShortcut,
  formatLocalDate,
  updateMomentLocaleToKorean,
  durationAsSecond,
  parseByUnix,
  parseByUtc,
  supressDeprecationWarnings,
  isMoment,
  diff,
  secondsToTimeFormat,
  minutesToHourFormat,
  secondToHourFormat,
  secondsToMinuteFormat,
};
